import { Component, createEffect, createSignal, mergeProps } from 'solid-js';
import {
  ICON_CONTEXT_MENU_DEFAULTS,
  IconContextMenuSettings,
} from './IconContextMenu.types';
import {
  ContextMenuItemConfig,
  ContextMenuItemMetaData,
} from '../project-data-manager/interfaces';
import DataItemContextMenu from '../project-data-manager/DataItemContextMenu';
import { computePosition, shift } from '@floating-ui/dom';

export * from './IconContextMenu.types';

const LIST_ITEM_PADDING = 0.5; // in rem, needs to be in sync with styling from figma
const REM_WIDTH_IN_PX = 16; // 1rem = 16px

export const IconContextMenu: Component<Partial<IconContextMenuSettings>> = (
  props: Partial<IconContextMenuSettings>
) => {
  let menuButtonRef!: HTMLButtonElement;
  let menuRef: HTMLDivElement;

  const activeProps = mergeProps(ICON_CONTEXT_MENU_DEFAULTS, props);
  const [showContextMenu, setShowContextMenu] = createSignal(false);

  const contextMenuItems = (): ContextMenuItemConfig[] => {
    return activeProps.menuItems.map((x) => {
      const tmp: ContextMenuItemConfig = {
        label: x.label,
        reference: x.value,
        disabled: x.disabled ?? false,
        accessConfig: x.accessConfig,
        disabledReasonTooltip: x.disabledReasonTooltip,
        handleMenuAction: (
          _: Record<string, unknown>,
          metadata: ContextMenuItemMetaData
        ) => {
          if (activeProps.onSelection) {
            activeProps.onSelection({
              event: metadata.event,
              value: activeProps.value,
              menuItem: metadata.value,
            });
          }
          metadata.event.stopImmediatePropagation();
        },
      };
      return tmp;
    });
  };

  createEffect(() => {
    if (showContextMenu()) {
      const tabAligned = activeProps.tabAligned;
      void computePosition(menuButtonRef, menuRef!, {
        placement: activeProps.menuPlacement,
        middleware: [shift()],
      }).then(({ x, y }) => {
        let padding = 0;
        const offsetX =
          menuRef!.clientWidth / 2 - // eslint-disable-line no-magic-numbers
          menuButtonRef.clientWidth / 2 - // eslint-disable-line no-magic-numbers
          LIST_ITEM_PADDING * REM_WIDTH_IN_PX +
          1; //account for the 1px border of the menu.
        if (tabAligned) x = x - offsetX;

        const requiredSpace =
          menuRef!.clientHeight + menuButtonRef.clientHeight;
        const bottomPx =
          menuButtonRef.getBoundingClientRect().top + requiredSpace;

        if (bottomPx > window.innerHeight) {
          padding = -requiredSpace;
        }

        Object.assign(menuRef!.style, {
          left: `${x.toString()}px`,
          top: `${(y + padding).toString()}px`,
        });
      });
    }
  });

  createEffect(() => {
    if (showContextMenu()) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  });

  const handleClickOutside = (event: MouseEvent): void => {
    // The event target when clicking on the menu button after the menu is open
    // evaluates to the trb-civil-3d-viewer element. So instead, we need to check whether
    // the XY of the click is inside the bounding rectangel of the menu button.
    const menuButtonRect = menuButtonRef.getBoundingClientRect();
    const isClickInsideMenuButton =
      event.clientX >= menuButtonRect.left &&
      event.clientX <= menuButtonRect.right &&
      event.clientY >= menuButtonRect.top &&
      event.clientY <= menuButtonRect.bottom;

    if (
      // If the event target is inside the portal container
      menuRef!.contains(event.target as Node) ||
      // Or the portal container is the event target
      (event.target as HTMLElement).shadowRoot?.contains(menuRef!) ||
      isClickInsideMenuButton
    ) {
      return;
    }
    setShowContextMenu(false);
  };

  const handleVisibilityChange = (): void => {
    if (document.hidden) {
      setShowContextMenu(false);
    }
  };

  return (
    <div
      data-testid={
        activeProps.name ? `iconcontextmenu-${activeProps.name}` : null
      }
    >
      <button class="flex items-center" ref={menuButtonRef}>
        <modus-wc-icon
          data-testid="icon"
          name={activeProps.iconName}
          size={activeProps.iconSize}
          onClick={(e: Event) => {
            setShowContextMenu(!showContextMenu());
            e.stopImmediatePropagation();
          }}
        />
      </button>
      <DataItemContextMenu
        ref={menuRef!}
        show={showContextMenu()}
        onCloseMenu={() => setShowContextMenu(false)}
        contextMenuItems={contextMenuItems()}
        dataItem={{ value: activeProps.value }}
      />
    </div>
  );
};

export default IconContextMenu;
